import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { eventsApi, toDE } from "../api";
import { DartsEvent } from "../types";

interface EventsContextValue {
  events: DartsEvent[];
  isLoading: boolean;
  error: string | null;
  fetchEvents: () => Promise<void>;
}

const EventsContext = createContext<EventsContextValue | null>(null);

export const EventsProvider: React.FC<{
  isAuthenticated: boolean;
  children: React.ReactNode;
}> = ({ isAuthenticated, children }) => {
  const [events, setEvents] = useState<DartsEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const raw = await eventsApi.getAll();
      setEvents(raw.map(toDE));
    } catch {
      setError("イベントの取得に失敗しました");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchEvents();
    } else {
      setEvents([]);
    }
  }, [isAuthenticated, fetchEvents]);

  return (
    <EventsContext.Provider value={{ events, isLoading, error, fetchEvents }}>
      {children}
    </EventsContext.Provider>
  );
};

export const useEventsContext = (): EventsContextValue => {
  const ctx = useContext(EventsContext);
  if (!ctx)
    throw new Error("useEventsContext must be used within EventsProvider");
  return ctx;
};
